// PostEditor — editor Tiptap customizado pra LinkedIn.
//
// Features:
//  - Bold/italic com toolbar (saída em Unicode mathematical, não markdown/HTML)
//  - Bullet list / numbered list (render com • e 1. 2.)
//  - Link inline
//  - Contador de caracteres
//  - Indicador de "ver mais" cutoff (~210 chars)
//  - Hashtags e mentions são plain text (LinkedIn detecta)

import { useEditor, EditorContent, type JSONContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import Link from "@tiptap/extension-link";
import { Bold, Italic, List, ListOrdered, Link as LinkIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  serializeToLinkedIn,
  countCharsForLinkedIn,
  LINKEDIN_TRUNCATE_AT,
  LINKEDIN_MAX_CHARS,
} from "../lib/linkedin-format";

/**
 * Converte string plain text (com \n\n separando blocos) em JSONContent do Tiptap.
 * Sem isso, Tiptap.setContent(string) trata como HTML inline e perde quebras.
 * Mantém JSONContent intacto se for passado direto.
 */
export const parseInitialContent = (
  input: string | JSONContent | undefined
): JSONContent | string | undefined => {
  if (!input || typeof input !== "string") return input;
  const paragraphs = input.split(/\n{2,}/);
  return {
    type: "doc",
    content: paragraphs.map((p) => {
      const trimmed = p.trim();
      if (!trimmed) return { type: "paragraph" };
      // multi-line (ex: lista bulleted): split em texts com hardBreak
      const lines = trimmed.split(/\n/);
      if (lines.length === 1) {
        return {
          type: "paragraph",
          content: [{ type: "text", text: lines[0] }],
        };
      }
      const nodes: JSONContent[] = [];
      lines.forEach((line, i) => {
        if (i > 0) nodes.push({ type: "hardBreak" });
        if (line) nodes.push({ type: "text", text: line });
      });
      return { type: "paragraph", content: nodes };
    }),
  };
};

/** Serializes any accepted content input to the LinkedIn plain-text form. */
const toLinkedInText = (input: JSONContent | string | undefined): string =>
  input
    ? serializeToLinkedIn(
        typeof input === "string"
          ? (parseInitialContent(input) as JSONContent) ?? { type: "doc", content: [] }
          : input
      )
    : "";

interface PostEditorProps {
  initialContent?: JSONContent | string;
  onChange?: (linkedinText: string, doc: JSONContent) => void;
}

export const PostEditor = ({ initialContent, onChange }: PostEditorProps) => {
  const [linkedinText, setLinkedinText] = useState(() => toLinkedInText(initialContent));
  // True while we push server content into the editor, so onUpdate doesn't
  // mistake a programmatic setContent for a user edit (which would autosave).
  const isProgrammaticRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2] } }),
      Placeholder.configure({ placeholder: "Escreva o post..." }),
      CharacterCount.configure({ limit: LINKEDIN_MAX_CHARS * 2 }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: parseInitialContent(initialContent),
    onUpdate: ({ editor }) => {
      const doc = editor.getJSON();
      const text = serializeToLinkedIn(doc);
      setLinkedinText(text);
      if (isProgrammaticRef.current) return;
      onChange?.(text, doc);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-slate max-w-none focus:outline-none min-h-[400px] px-4 py-3 text-base leading-relaxed [&_p]:my-3 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_ul]:my-3 [&_ol]:my-3 [&_li]:my-1",
      },
    },
  });

  // Re-sync the editor when the server content changes for the same draft
  // (e.g. an alternative was promoted and the draft refetched). Runs only when
  // the incoming content differs from what the editor already holds, so a user
  // mid-edit is never clobbered and no infinite loop occurs. setContent with
  // emitUpdate=false suppresses onUpdate, so this doesn't trigger an autosave.
  useEffect(() => {
    if (!editor) return;
    const nextText = toLinkedInText(initialContent);
    if (nextText === serializeToLinkedIn(editor.getJSON())) return;
    isProgrammaticRef.current = true;
    editor.commands.setContent(parseInitialContent(initialContent) ?? "", false);
    setLinkedinText(nextText);
    isProgrammaticRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, initialContent]);

  const charCount = countCharsForLinkedIn(linkedinText);
  const overLimit = charCount > LINKEDIN_MAX_CHARS;
  const beyondCutoff = charCount > LINKEDIN_TRUNCATE_AT;

  if (!editor) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1 border-b border-slate-200 px-2 py-1.5 bg-slate-50">
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Negrito (Unicode 𝗯𝗼𝗹𝗱)"
        >
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Itálico (Unicode 𝘪𝘵𝘢𝘭𝘪𝘤)"
        >
          <Italic size={16} />
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Lista com bullets"
        >
          <List size={16} />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Lista numerada"
        >
          <ListOrdered size={16} />
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          active={editor.isActive("link")}
          onClick={() => {
            const url = window.prompt("URL:");
            if (url) editor.chain().focus().setLink({ href: url }).run();
          }}
          title="Link"
        >
          <LinkIcon size={16} />
        </ToolbarButton>

        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500 pr-2">
          {beyondCutoff && !overLimit && <span className="text-amber-600">passou do "ver mais"</span>}
          <span className={overLimit ? "text-red-600 font-medium" : ""}>
            {charCount}/{LINKEDIN_MAX_CHARS}
          </span>
        </div>
      </div>

      <EditorContent editor={editor} />

      {/* Aviso de acessibilidade — bold/italic em Unicode quebra leitor de tela */}
      {(editor.isActive("bold") || editor.isActive("italic")) && (
        <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 text-xs text-amber-800">
          ⚠️ Negrito/itálico em Unicode não é lido por leitores de tela. Use com parcimônia.
        </div>
      )}
    </div>
  );
};

const ToolbarButton = ({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded hover:bg-slate-200 transition ${
        active ? "bg-slate-200 text-slate-900" : "text-slate-600"
      }`}
    >
      {children}
    </button>
  );
};

const Divider = () => {
  return <div className="w-px h-5 bg-slate-300 mx-1" />;
};
